/* =========================================================================
   MARKETING Hero — Sales Page Engine: shared types & constants.
   The engine must go through the 5-Layer workflow (Research → Strategy →
   Architecture → Full Copy → Visual/UX) + Conversion QC — never jump
   straight to writing sections. Copy adapts to Awareness × Traffic Temp.
   ========================================================================= */

export const AWARENESS_LEVELS: { id: string; label: string }[] = [
  { id: "unaware", label: "ไม่รู้ตัวว่ามีปัญหา (Unaware)" },
  { id: "problem_aware", label: "รู้ว่ามีปัญหา (Problem Aware)" },
  { id: "solution_aware", label: "รู้ว่ามีทางแก้ (Solution Aware)" },
  { id: "product_aware", label: "รู้จักสินค้าเรา (Product Aware)" },
  { id: "most_aware", label: "พร้อมซื้อ (Most Aware)" },
];

export const TRAFFIC_TEMPS: { id: string; label: string }[] = [
  { id: "cold", label: "Cold — ไม่เคยรู้จัก" },
  { id: "warm", label: "Warm — เคยเห็น/สนใจ" },
  { id: "hot", label: "Hot — พร้อมตัดสินใจ" },
  { id: "retargeting", label: "Retargeting — เคยเข้ามาแล้ว" },
];

export const SECTION_TYPES: { id: string; label: string }[] = [
  { id: "hero", label: "Hero" },
  { id: "problem", label: "Problem" },
  { id: "pain_amplification", label: "Pain Amplification" },
  { id: "cost_of_inaction", label: "Cost of Inaction" },
  { id: "desired_future", label: "Desired Future" },
  { id: "new_opportunity", label: "New Opportunity" },
  { id: "old_vs_new", label: "Old Way vs New Way" },
  { id: "solution", label: "Solution" },
  { id: "unique_mechanism", label: "Unique Mechanism" },
  { id: "why_it_works", label: "Why It Works" },
  { id: "benefits", label: "Benefits" },
  { id: "deliverables", label: "Deliverables / Scope" },
  { id: "who_for", label: "Who It Is For" },
  { id: "who_not_for", label: "Who It Is Not For" },
  { id: "proof", label: "Proof" },
  { id: "review", label: "Review" },
  { id: "case_study", label: "Case Study" },
  { id: "authority", label: "Authority" },
  { id: "process", label: "Process" },
  { id: "objection_handling", label: "Objection Handling" },
  { id: "value_stack", label: "Value Stack" },
  { id: "price", label: "Price" },
  { id: "bonus", label: "Bonus" },
  { id: "guarantee", label: "Guarantee" },
  { id: "risk_reversal", label: "Risk Reversal" },
  { id: "urgency", label: "Urgency" },
  { id: "faq", label: "FAQ" },
  { id: "final_close", label: "Final Close" },
  { id: "cta", label: "CTA" },
];
export const sectionLabel = (id: string) => SECTION_TYPES.find((s) => s.id === id)?.label ?? id;

export interface SalesPageInputs {
  product?: string; price?: string; target?: string;
  trafficSource?: string; trafficTemperature?: string; awarenessLevel?: string; marketSophistication?: string;
  pain?: string; desire?: string; objection?: string; usp?: string; offer?: string;
  proof?: string; authority?: string; caseStudy?: string; review?: string;
  guarantee?: string; bonus?: string; scarcity?: string; urgency?: string;
  cta?: string; conversionGoal?: string; funnel?: string; competitor?: string;
  name?: string;
}

export interface ScoredAngle { name: string; type: string; why: string; score: number; top?: boolean; }
export interface ScoredHeadline { text: string; category: string; score: number; top?: boolean; }

export interface SalesSection {
  type: string;
  purpose: string;
  readerBefore: string;
  readerAfter: string;
  coreMessage: string;
  copy: string;
  proofNeeded: string;
  objectionAddressed: string;
  ctaTransition: string;
  visualDirection: string;
}

export interface QCCheck { item: string; verdict: "pass" | "warn" | "fail"; note: string; }

export interface SalesPageOutput {
  meta: { awarenessLevel: string; trafficTemperature: string; conversionGoal: string };
  layer1_research: {
    customerInsight: string;
    painDug: string[];
    desireDug: string[];
    objectionsDug: { text: string; mappedSection: string }[];
    hypotheses: string[];
  };
  layer2_strategy: {
    customerTruth: string; coreProblem: string; rootProblem: string; enemyOldWay: string;
    newOpportunity: string; desiredTransformation: string; bigIdea: string; corePromise: string;
    uniqueMechanism: string; reasonWhy: string; believability: string; primaryObjection: string;
    buyingTrigger: string; angles: ScoredAngle[]; headlines: ScoredHeadline[];
  };
  layer3_architecture: { flowRationale: string; order: string[] };
  sections: SalesSection[]; // Layer 4 (copy) + Layer 5 (visual) per section
  layer5_visual_overall: string;
  qc: { score: number; checks: QCCheck[] };
  disclaimer?: string;
}

export const SALESPAGE_SCHEMA_HINT = {
  meta: { awarenessLevel: "string", trafficTemperature: "string", conversionGoal: "string" },
  layer1_research: { customerInsight: "string", painDug: ["string"], desireDug: ["string"], objectionsDug: [{ text: "string", mappedSection: "section id" }], hypotheses: ["string (ระบุว่าเป็นสมมติฐาน)"] },
  layer2_strategy: {
    customerTruth: "string", coreProblem: "string", rootProblem: "string", enemyOldWay: "string",
    newOpportunity: "string", desiredTransformation: "string", bigIdea: "string", corePromise: "string",
    uniqueMechanism: "string", reasonWhy: "string", believability: "string", primaryObjection: "string",
    buyingTrigger: "string",
    angles: [{ name: "string", type: "string", why: "string", score: 0, top: true }],
    headlines: [{ text: "string", category: "pain|desire|result|opportunity|curiosity|authority|contrarian|question|transformation|mechanism", score: 0, top: true }],
  },
  layer3_architecture: { flowRationale: "string — อธิบายว่าทำไมเลือก Flow นี้ตาม Awareness/Funnel", order: ["section id ตามลำดับ"] },
  sections: [{ type: "section id", purpose: "string", readerBefore: "string", readerAfter: "string", coreMessage: "string", copy: "string — คอปปี้จริง เจาะจง ห้ามกว้าง", proofNeeded: "string", objectionAddressed: "string", ctaTransition: "string", visualDirection: "string" }],
  layer5_visual_overall: "string",
  qc: { score: 0, checks: [{ item: "string", verdict: "pass|warn|fail", note: "string" }] },
};
